import React, { useRef, useEffect } from 'react'
import { TextInput } from '@/components/common/text-input'
import {
  CalendarActiveIcon,
  CalendarIcon,
  SearchActiveIcon,
  SearchBtnIcon,
  SearchIcon
} from '@/components/agent-dashboard/assets';
import { useTranslation } from 'react-i18next';
import { Dropdown } from '@/components/ui/modal';
import { TextButton } from '@/components/common/text-button';
import { cn } from '@/lib/utils';
import { SearchFormData } from '@/manager/manager/agent-dashboard-content-manager';
import { DatePicker, DatePickerActions } from '../common/date-picker';
import { formatDateToYYYYMMDD } from '@/utils/date-formatter';

interface SearchBarProps {
  searchFormData: SearchFormData,
  search: (page: number) => void,
  resetAndSearch: () => void,
  changeSearchForm: (key: keyof SearchFormData, value: string | number | string[]) => void,
}

/** 智能体内容管理-搜索栏 */
export const SearchBar: React.FC<SearchBarProps> = ({
  searchFormData,
  search,
  resetAndSearch,
  changeSearchForm,
}) => {
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})
  const indicatorRef = useRef<HTMLDivElement>(null)
  const datePickerRef = useRef<DatePickerActions>(null);

  const { t } = useTranslation();

  // 监听tab变化，更新底部背景位置
  useEffect(() => {
    const activeTabElement = tabRefs.current[searchFormData.type];
    const indicatorElement = indicatorRef.current;

    if (activeTabElement && indicatorElement) {
      const { left, width } = activeTabElement.getBoundingClientRect();
      const containerLeft = activeTabElement.parentElement?.getBoundingClientRect().left || 0;
      const offsetLeft = left - containerLeft;

      indicatorElement.style.transform = `translateX(${offsetLeft}px)`;
      indicatorElement.style.width = `${width}px`;
    }
  }, [searchFormData.type]);

  // Tab选项定义
  const tabs = [
    { key: 999, label: t('agent_dashboard.content_management.search.type.all') },
    { key: 1, label: t('agent_dashboard.content_management.search.type.video') },
    { key: 2, label: t('agent_dashboard.content_management.search.type.podcast') },
    { key: 3, label: t('agent_dashboard.content_management.search.type.moments') },
    { key: 4, label: t('agent_dashboard.content_management.search.type.image') },
    { key: 5, label: t('agent_dashboard.content_management.search.type.article') },
    { key: 6, label: t('agent_dashboard.content_management.search.type.ai_creation') },
    { key: 7, label: t('agent_dashboard.content_management.search.type.ai_speech') }
  ];

  const handleSearch = () => {
    search(1);
  }

  const handleReset = () => {
    resetAndSearch();
    datePickerRef.current?.reset();
  }

  const handleChangeTab = (tabKey: number) => {
    changeSearchForm('type', tabKey);
    search(1);
  }
  return (
    <div>
      <div className='flex flex-wrap gap-3 mb-4'>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.content_management.search.conditional_filtering')}</label>
          <Dropdown
            value={searchFormData.searchType}
            placeholder={t('agent_dashboard.content_management.search.please_select')}
            className='h-9'
            onValueChange={(value) => changeSearchForm('searchType', value)}
            width='11.3rem'
            options={[
              { value: 'title', label: t('agent_dashboard.content_management.search.filter.title') },
              { value: 'user_name', label: t('agent_dashboard.content_management.search.filter.user_name') },
              { value: 'user_id', label: t('agent_dashboard.content_management.search.filter.user_id') },
            ]}
          />
        </div>
        <div className='flex items-end'>
          <TextInput
            value={searchFormData.searchValue}
            placeholder={t('agent_dashboard.content_management.search.search_placeholder')}
            className='w-[25rem] h-9 flex-1 font-normal'
            prefixIcon={<SearchIcon className='w-5 h-5' />}
            prefixActiveIcon={<SearchActiveIcon className='w-5 h-5' />}
            onChange={(e) => changeSearchForm('searchValue', e.target.value)}
          />
        </div>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>
            {t('agent_dashboard.content_management.search.source')}
          </label>
          <Dropdown
            value={searchFormData.source?.toString() || ''}
            placeholder={t('agent_dashboard.content_management.search.select_placeholder')}
            className='h-9 font-medium'
            defaultValue='all'
            onValueChange={(value) => changeSearchForm('source', Number(value))}
            width='10rem'
            options={[
              { value: '1', label: t('agent_dashboard.content_management.search.source_map.user') },
              { value: '0', label: t('agent_dashboard.content_management.search.source_map.auto') },
            ]}
          />
        </div>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>
            {t('agent_dashboard.content_management.search.push_status')}
          </label>
          <Dropdown
            value={searchFormData.pushState?.toString() || ''}
            placeholder={t('agent_dashboard.content_management.search.select_placeholder')}
            className='h-9 font-medium'
            defaultValue='all'
            onValueChange={(value) => changeSearchForm('pushState', value)}
            width='10rem'
            // options={[
            //   { value: 'viewed', label: t('agent_dashboard.content_management.search.status.viewed') },
            //   { value: 'pushed', label: t('agent_dashboard.content_management.search.status.pushed') },
            //   { value: 'pending_push', label: t('agent_dashboard.content_management.search.status.pending_push') },
            //   { value: 'push_failed', label: t('agent_dashboard.content_management.search.status.push_failed') },
            // ]}
            options={[
              { value: '0', label: t('agent_dashboard.content_management.search.new_status.processing') },
              { value: '1', label: t('agent_dashboard.content_management.search.new_status.finished') },
              { value: '2', label: t('agent_dashboard.content_management.search.new_status.failed') },
              { value: '3', label: t('agent_dashboard.content_management.search.new_status.cancelled') },
              // { value: '9', label: t('agent_dashboard.content_management.search.new_status.deleted') },
              { value: '10', label: t('agent_dashboard.content_management.search.new_status.timeout') },
            ]}
          />
        </div>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.user_management.search.create_start_date')}</label>
          <DatePicker
            range
            ref={datePickerRef}
            startTrigger={(
              <TextInput
                readOnly
                value={searchFormData.startTime}
                prefixIcon={<CalendarIcon className='w-5 h-5' />}
                prefixActiveIcon={<CalendarActiveIcon className='w-5 h-5' />}
                placeholder={t('agent_dashboard.user_management.search.please_select')}
                className='w-[11.3rem] h-9 font-normal'
              />
            )}
            endTrigger={(
              <div className='relative'>
                <label className='absolute bottom-9 left-0 text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.user_management.search.create_end_date')}</label>
                <TextInput
                  readOnly
                  value={searchFormData.endTime}
                  prefixIcon={<CalendarIcon className='w-5 h-5' />}
                  prefixActiveIcon={<CalendarActiveIcon className='w-5 h-5' />}
                  placeholder={t('agent_dashboard.user_management.search.please_select')}
                  className='w-[11.3rem] h-9 font-normal'
                />
              </div>
            )}
            onConfirm={(range) => {
              changeSearchForm('startTime', [
                formatDateToYYYYMMDD(range[0]),
                formatDateToYYYYMMDD(range[1]),
              ]);
            }}
          />
        </div>
        <div className='flex items-end space-x-3'>
          <TextButton
            label={t('agent_dashboard.user_management.search.reset')}
            className={cn(
              'max-w-max !px-5 h-9 bg-white',
              'font-semibold text-sm !text-[rgba(65,70,81,1)]',
              'border border-[rgba(213, 215, 218, 1)] hover:bg-gray-50',
            )}
            onClick={handleReset}
          />
          <TextButton
            label={t('agent_dashboard.user_management.search.search')}
            icon={<SearchBtnIcon className='w-5 h-5' />}
            className={cn(
              '!w-[6.4rem] h-9 ',
              'text-white font-semibold text-sm',
            )}
            onClick={handleSearch}
          />
        </div>
      </div>
      {/* type切换区域 */}
      <div className="relative border-b border-[rgba(224,227,230,1)]">
        <div className="flex overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              ref={(el) => (tabRefs.current[tab.key] = el)}
              className={cn(
                'px-4 py-3 text-sm font-medium transition-all whitespace-nowrap',
                searchFormData.type === tab.key ? 'text-[#101828] font-medium' : 'text-[rgba(129,133,137,1)]',
              )}
              onClick={() => handleChangeTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* 底部激活横条 */}
        <div
          ref={indicatorRef}
          className="absolute bottom-0 left-0 h-[2px] bg-[#101828] transition-all duration-300 ease-out"
          style={{ width: '0px', transform: 'translateX(0px)' }}
        />
      </div>
    </div>
  )
}
